import { Injectable } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable } from 'rxjs';
import { IWorkflow } from './models/workflow.model';
import { processingString } from './capture-strings';
import { processingString_noBinarization } from './capture-strings';
import { processingString_noCrop } from './capture-strings';
import { processingString_noCrop_noBinarization } from './capture-strings';

@Injectable({
  providedIn: 'root'
})
export class CaptureProcessingService {

	private logger: Logger;
	public workflow: IWorkflow;
	public workflowList: Array<IWorkflow>;

	private imageprocessSuccessCB = null;
	private imageprocessErrorCB = null;

	//Reference variable for ImageProcessor Options
	private imageProcessingOptions = null;
	//Alias name for kfxCordova.kfxEngine.ImageProcessor
	private imageProcessor = null;
	private capturedImage = null;
	private processedImage = null;

	constructor(
		private loggingService: LoggingService,
	) {
		this.logger = this.loggingService.getLogger("[CaptureProcessingService]");
		const methodName = "ctor";
		this.logger.entry(methodName);
	}

// #############################################################################################################################################################################
// #############################################################################################################################################################################
// ### 		PROCESS_IMAGE_OPTIONS        #########################################################################################################################################
// #############################################################################################################################################################################
// #############################################################################################################################################################################

   public setImageProcessingOptions(
	imageProcessingSetting: string,
	isImageManuallyCropped: boolean,
	workflow: IWorkflow,
	boundingTetragon
	): Observable<any> {
		this.logger.info('setImageProcessingOptions()');
		return new Observable(observer => {
			if (imageProcessingSetting === 'ippString') {
				this.imageProcessingOptions = this.getDefaultProcessorOptions();
				this.workflow = workflow;
				isImageManuallyCropped = this.checkImageManuallyCropped(isImageManuallyCropped);
				this.logger.info('options.ImageProcessorConfiguration.ippString: ' + this.imageProcessingOptions.ImageProcessorConfiguration.ippString);

				observer.next(isImageManuallyCropped);
				observer.complete();

			} else {
				this.imageProcessingOptions = this.initializeBasicSettingsProfile(imageProcessingSetting, boundingTetragon); // For cropping or rotating image, set a BasicSettingsProfile
				observer.next(isImageManuallyCropped);
				observer.complete();
			}
		});
	}

	private checkImageManuallyCropped(isImageManuallyCropped) {
		// do not crop image if user has cropped image.
		this.imageProcessingOptions.ImageProcessorConfiguration.ippString = isImageManuallyCropped
		? this.setImageTypeProcessingString_noCrop()
		: this.setImageTypeProcessingString();

		return false;
	}

	// Set the image processing string for searchable PDF or Image only PDF based on the workflow configuration
	private setImageTypeProcessingString_noCrop() {
		return !this.workflow.ocrConfiguration ? processingString_noCrop_noBinarization : processingString_noCrop;
	}

	private setImageTypeProcessingString() {
		return !this.workflow.ocrConfiguration ? processingString_noBinarization : processingString;
	}

 	public getDefaultProcessorOptions() {
		this.logger.info('getDefaultProcessorOptions()');
		this.imageProcessor = window['kfxCordova'].kfxEngine.createImageProcessor();
		const processingOptions =  this.imageProcessor.getImageProcessingOptions();
		processingOptions.ProcessedImage.representation = 'IMAGE_REP_BOTH';
		return processingOptions;
	}

	private initializeBasicSettingsProfile(imageProcessorSetting, boundingTetragon) {
		this.logger.info('initializeBasicSettingsProfile()');
		/**
		 * Image processing engine that can process a single image according to settings contained in either an ImagePerfectionProfile object, or a BasicSettingsProfile object,
		 * but not both simultaneously.The settings in the ImagePerfectionProfile property take precedence over those in the BasicSettingsProfile property.
		 *
		 */

		const imageProcessingOptions = this.imageProcessor.getImageProcessingOptions();

		// which identifies the Image Representation(s) included in this Image Object.
		imageProcessingOptions.ProcessedImage.representation = "IMAGE_REP_BITMAP";
		// IMAGE_REP_NONE The image object has no image content.
		// IMAGE_REP_BITMAP The image object references a bitmap.
		// IMAGE_REP_FILE The image object references a file.
		// IMAGE_REP_BOTH The image object references both a bitmap and a file.
		///which identifies the Mime-Type of the image file referenced by this Image Object.
		imageProcessingOptions.ProcessedImage.mimeType = "MIMETYPE_JPEG"; // MIMETYPE_JPEG,MIMETYPE_PNG,MIMETYPE_TIFF,MIMETYPE_UNKNOWN
		imageProcessingOptions.ProcessedImage.jpegQuality = 90;

		//Don't set ImagePerfectionProfile options if you want to use BasicSettingsProfile.
		imageProcessingOptions.ImagePerfectionProfile = null;
		imageProcessingOptions.ImageProcessorConfiguration = null;

		///which identifies the rotation type.
		if (imageProcessorSetting === 'rotateImage') {
			imageProcessingOptions.BasicSettingsProfile.rotateType = "ROTATE_90"; //ROTATE_NONE,ROTATE_90,ROTATE_180,ROTATE_270,ROTATE_AUTO
		} else {
			imageProcessingOptions.BasicSettingsProfile.rotateType = "ROTATE_NONE";
		}

		///which identifies the crop type.
		if (imageProcessorSetting === 'cropImage') {
			imageProcessingOptions.BasicSettingsProfile.cropType = "CROP_TETRAGON";
			imageProcessingOptions.BasicSettingsProfile.BoundingTetragon = boundingTetragon;
			/*CROP_NONE -No Crop,
			CROP_AUTO - Automatically crop to the edges of the page found in the image, assuming the image has page edges,
			CROP_TETRAGON - Indicate you want the image cropped to a user supplied tetragon, as provided in the BoundingTetragon property
			CROP_QUICKANALYSIS -when you have previously performed a quick analysis of an input image, and the cropping results are returned in the input image object. Use this option to use these page boundaries, which will reduce image processing time. The Image Processor uses the image meta data stored in the image object after the quick analysis is performed. */
		} else {
			imageProcessingOptions.BasicSettingsProfile.cropType = "CROP_NONE";
		}

		///The deskew property specifies whether to deskew the image or not.
		imageProcessingOptions.BasicSettingsProfile.inputDocShortEdge = 0.0;
		imageProcessingOptions.BasicSettingsProfile.inputDocLongEdge = 0.0;
		imageProcessingOptions.BasicSettingsProfile.doDeskew = true;
		// Use this property to set the desired output image DPI (Dots Per Inch). If you set it to 0 or null, the library will automatically detect the output image DPI and indicate it in the output object.For good extraction results minimum 96 DPI is required.
		imageProcessingOptions.BasicSettingsProfile.outputDPI = 0;
		///which identifies the output bit depth.
		imageProcessingOptions.BasicSettingsProfile.outputBitDepth = "COLOR"; //BITONAL,GRAYSCALE,COLOR

		console.log('imageProcessingOptions', imageProcessingOptions);
		return imageProcessingOptions;
	}

	public specifyImageFilePath(processedImage, capturedImage): Observable<any> {
		this.logger.info('specifyImageFilePath()');
		this.capturedImage = capturedImage;
		this.processedImage = processedImage;
		return new Observable((observer) => {
			function successCallback(response) {
				observer.next(response);
				observer.complete();
			}

			function errorCallback(error) {
				alert(JSON.stringify(error));
				observer.next(error);
				observer.complete();
			}

			const imgObj = (processedImage ? processedImage : capturedImage);

			this.imageProcessor.specifyProcessedImageFilePath(successCallback, errorCallback, imgObj.imgID + '.jpg');
		});
	}

// #############################################################################################################################################################################
// #############################################################################################################################################################################
// ### 		PROCESS_IMAGE        #########################################################################################################################################
// #############################################################################################################################################################################
// #############################################################################################################################################################################

	public doProcess(successCallback, errorCallback) {
		this.logger.info('doProcess()');
		this.imageProcessingOptions = this.imageProcessingOptions;
		this.addImageProcessorListeners();
		this.imageprocessSuccessCB = successCallback;
		this.imageprocessErrorCB = errorCallback;
	}

	/*
	 * Add all Image processing Listeners
	 *
	 */
	public addImageProcessorListeners() {
		this.logger.info('addImageProcessorListeners()');

		/** This method would receive the on image processed at the ImageProcessor.
         imageOutHandler: The processed image is returned in this callback.
         */
         this.imageProcessor.addImageOutEventListener(
			(imageOutSuccess) => {
				//Set the properties of the image processor
				this.setProcessorOptions();
				console.log("Image out listener registered success callback " + JSON.stringify(imageOutSuccess));
			},
			(imageOutError) => {
				this.sendCallback(this.imageprocessErrorCB, imageOutError);
				console.log("Image out listener error callback " + JSON.stringify(imageOutError));
			},
			this.imageOutHandler.bind(this)
		);

		/** This method would receive the Image process progress at the ImageProcessor.
        	processProgressHandler:Callback with a variable value representing Image processing progress will be returned.
         */
      this.imageProcessor.addProcessProgressListener(
  			(processProgressSuccess) => {
  				console.log(
  					"Process progress listener registered success callback " + JSON.stringify(processProgressSuccess)
  				);
  			},
  			(processProgressError) => {
  				alert("Process progress listener error callback " + JSON.stringify(processProgressError));
  				console.log("Process progress listener error callback " + JSON.stringify(processProgressError));
  			},
  			this.processProgressHandler.bind(this)
		);
	}

	//Set the properties of the image processor
	public setProcessorOptions() {
		this.logger.info('setProcessorOptions()');

		/** Set the Options/properties of the ImageProcessor.
		 * imageProcessorOptionsCB is a success callback.In this callback method will call the processImage.
		 * 'imageProcessorOptions'  variable containing the properties  to be set to the ImageProcessor.
		 */
     	this.imageProcessor.setImageProcessorOptions(
			this.imageProcessorOptionsCB.bind(this),
			function (result) {
				this.sendCallback(this.imageprocessErrorCB, result);
			},
			this.imageProcessingOptions
		);
	}

	/*
	 * ImageProcessorOptions Success callback
	 *
	 */

	public imageProcessorOptionsCB(message) {
		this.logger.info('imageProcessorOptionsCB()');

		/***************************************************
		 *
		 *
		 *
		 * ImageProcess method
		 *
		 *
		 *
		 * *************************************************
		 */

		/**
		 * This method when you want to perform standard image processing on the image id supplied with the method. The ImageProcessor
		 *	processes the image using the processing options contained in the profile you specified. You can specify either a basic
		 *	settings profile or an image perfection profile.
		 *  After processing the image, imageOutHandler("imageOutCallback") will get the processed image.
		 */

		const imgObj = (this.processedImage ? this.processedImage : this.capturedImage);

		this.imageProcessor.processImage(
			(result) => {
				console.log("Success! processImage" + result);
			},
			(result) => {
				 this.sendCallback( this.imageprocessErrorCB, result);
				alert("Error! processImage " + JSON.stringify(result));
			},
			this.capturedImage.imgID
		);
	}

	/*
	 * will be invoked, once image processing gets completed
	 *
	 * @gets processed image imageId
	 *
	 */
	public imageOutHandler(ImgObject) {
		this.logger.info('imageOutHandler()');
		this.removeImageProcessorListeners();
		 this.sendCallback(this.imageprocessSuccessCB, ImgObject);
	}

	/*
	 * logs image processing percentage
	 *
	 * @gets image processing percentage
	 *
	 */
	public processProgressHandler(message) {
		this.logger.info('processProgressHandler() :: ' + message.ProgressPercent);
		// this.dialogService.showLoadingSpinnerWithMessage('Processing image... ' + message.ProgressPercent + ' %', 'procecessProgressHandler()')
		console.log("calling processProgressHandler from app.js ProgressPercent :: " + message.ProgressPercent);
	}

		/*
	 * Removes All Image processing Listeners
	 *
	 */
	public removeImageProcessorListeners() {
		this.logger.info('removeImageProcessorListeners()');

		/** The method would remove the listener and would not receive the processed image events. After removing the listener,
		there will not be any call backs from native .
			*/
		this.imageProcessor.removeImageOutEventListener(
			(imageOutSuccess) => {
				console.log("Remove image out listener success callback " + JSON.stringify(imageOutSuccess));
			},
			(imageOutError) => {
				alert("Remove image out listener error callback " + JSON.stringify(imageOutError));
				console.log("Remove image out listener error callback " + JSON.stringify(imageOutError));
			}
		);

		/** The method would remove the listener and would not receive the Processed image progress events. After removing the listener,
		there will not be any call backs from native */

		this.imageProcessor.removeProcessProgressListener(
			(processProgressSuccess) => {
				console.log("Remove process progress listener success callback " + JSON.stringify(processProgressSuccess));
			},
			(processProgressError) => {
				alert("Remove process progress listener error callback " + JSON.stringify(processProgressError));
				console.log("Remove process progress listener error callback " + JSON.stringify(processProgressError));
			}
		);
	}

	public sendCallback(callback, data) {
		this.logger.info('sendCallback()');
		if (typeof callback === "function") {
			callback(data);
		}
	}
}
